import clsx from "clsx";
import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useLogout } from "../../hooks/useLogout";
import { Link } from "../link/Link";

import logo from "../app/logo.svg";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ADMIN_ANNOUNCEMENTS_ROUTE,
  ADMIN_BRANDED_APPS_ROUTE,
  ADMIN_EXERCISES_ROUTE,
  ADMIN_JOURNEY_TEMPLATES_ROUTE,
  ADMIN_PROGRAMS_ROUTE,
  ADMIN_TEMPLATES_ROUTE,
  ADMIN_USERS_ROUTE,
} from "../../routes/routes";
import {
  Dumbbell,
  LogOut,
  Users,
  Calendar,
  LayoutTemplate,
  Workflow,
  Megaphone,
  Smartphone,
} from "lucide-react";

const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    [theme.breakpoints.up("md")]: {
      width: theme.adminDrawer.width,
      zIndex: 1,
    },
    borderRight: "1px solid",
    borderRightColor: theme.palette.border.primary,
  },
  navItem: {
    color: theme.palette.text.primary,
    margin: theme.spacing(0.5, 0),
    display: "flex",
    justifyContent: "center",
  },
  navItemSelected: {
    backgroundColor: theme.palette.selected.main,
  },
}));

function AppDrawer(props) {
  const { className, open, location, ...other } = props;
  const { breakpoints } = useTheme();
  const mediaQuery = `(min-width:${breakpoints.values.md}px)`;
  const mdUp = useMediaQuery(mediaQuery);
  const s = useStyles();
  const logout = useLogout({ immediate: true });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isSelected = React.useCallback(
    (navPath) =>
      location.pathname === navPath ||
      location.pathname.startsWith(`${navPath}/`),
    [location.pathname],
  );

  const handleLogOut = React.useCallback(
    (event) => {
      event.preventDefault();
      logout();
    },
    [logout],
  );

  const handleHome = React.useCallback((event) => {
    event.preventDefault();
    queryClient.clear();
    navigate("/");
  }, []);

  const menuItems = React.useMemo(
    () => [
      {
        text: "Home",
        link: "/",
        onClick: handleHome,
        icon: <img src={logo} alt="logo" />,
      },
      {
        text: "Users",
        link: ADMIN_USERS_ROUTE,
        icon: <Users />,
      },
      {
        text: "Announcements",
        link: ADMIN_ANNOUNCEMENTS_ROUTE,
        icon: <Megaphone />,
      },
      {
        text: "Exercises",
        link: ADMIN_EXERCISES_ROUTE,
        icon: <Dumbbell />,
      },
      {
        text: "Programs",
        link: ADMIN_PROGRAMS_ROUTE,
        icon: <Calendar />,
      },
      {
        text: "Templates",
        link: ADMIN_TEMPLATES_ROUTE,
        icon: <LayoutTemplate />,
      },
      {
        text: "Journey Templates",
        link: ADMIN_JOURNEY_TEMPLATES_ROUTE,
        icon: <Workflow />,
      },
      {
        text: "Branded Apps",
        link: ADMIN_BRANDED_APPS_ROUTE,
        icon: <Smartphone />,
      },
      {
        link: "/logout",
        text: "Log out",
        onClick: handleLogOut,
        icon: <LogOut />,
      },
    ],
    [handleLogOut],
  );

  return (
    <Drawer
      className={clsx(s.root, className)}
      {...other}
      classes={{ paper: s.paper }}
      open={mdUp ? true : open}
      variant={mdUp ? "permanent" : "temporary"}
      anchor="left"
    >
      <List>
        {menuItems.map((x) => (
          <ListItemButton
            key={x.link}
            className={clsx(s.navItem, {
              [s.navItemSelected]: isSelected(x.link),
            })}
            component={Link}
            href={x.link}
            onClick={x.onClick || props.onClose}
            underline="none"
          >
            {x.icon}
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );
}

const AdminDrawer = AppDrawer;
export { AdminDrawer };
